import { useEffect, useState } from 'react';

import type { LocationMappingResponse } from '@revelio/data-access';

import { type GeoLevel, type GeoType, parseId } from './useResolveProfileIds';

// ── Types ──

export interface ResolvedLocation {
  geoLevels: GeoLevel[];
}

export const EMPTY_LOCATION: ResolvedLocation = { geoLevels: [] };

// ── Helpers ──

const LOCATION_MAPPER_URL = '/api/ai-search/mapper/location';

const fetchLocationMapping = async (
  location: string,
  signal?: AbortSignal
): Promise<LocationMappingResponse> => {
  const response = await fetch(LOCATION_MAPPER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ locations: [location] }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Location mapper request failed (${response.status})`);
  }

  return (await response.json()) as LocationMappingResponse;
};

/** Look up a geo display name from a selection list by ID, with a fallback. */
const lookupGeoName = (
  list:
    | Array<{
        id?: string | number | null;
        shortName?: string | null;
        longName?: string | null;
      }>
    | undefined,
  targetId: number,
  fallbackName: string
): string => {
  if (!list) return fallbackName || `Geo #${targetId}`;
  const match = list.find((item) => parseId(item.id) === targetId);
  return (match?.longName ||
    match?.shortName ||
    fallbackName ||
    `Geo #${targetId}`) as string;
};

export type GeoSelectionLists = Record<
  string,
  Array<{
    id?: string | number | null;
    shortName?: string | null;
    longName?: string | null;
  }>
>;

// ── Hook ──

/**
 * Custom hook that calls the Location Mapping V2 API and resolves the result
 * to a set of GeoLevel entries with exact numeric IDs.
 *
 * The mapper returns metro_area_id, country_id, region_id, city_id which are
 * exact taxonomy IDs from the Revelio geo taxonomy — no fuzzy matching needed.
 *
 * We cross-reference against the geo selection lists to get display names.
 */
export const useLocationMapping = (
  location: string | null | undefined,
  geoLists: GeoSelectionLists | null
): { location: ResolvedLocation; isLoading: boolean } => {
  const [resolved, setResolved] = useState<ResolvedLocation>(EMPTY_LOCATION);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!location) {
      setResolved(EMPTY_LOCATION);
      return;
    }

    if (!geoLists) return;

    const controller = new AbortController();
    setIsLoading(true);

    const resolve = async () => {
      try {
        const apiResponse = await fetchLocationMapping(
          location,
          controller.signal
        );

        if (apiResponse.results && apiResponse.results.length > 0) {
          const result = apiResponse.results[0];
          if (!result) {
            setResolved(EMPTY_LOCATION);
            return;
          }

          const geoLevels: GeoLevel[] = [];

          // City level
          if (result.city_core_id > 0) {
            const cityName = lookupGeoName(
              geoLists['city'],
              result.city_core_id,
              result.city || result.city_core
            );
            geoLevels.push({
              id: result.city_core_id,
              type: 'city' as GeoType,
              name: cityName,
            });
          }

          // MSA / Metro Area level
          if (result.metro_area_id > 0) {
            const msaName = lookupGeoName(
              geoLists['msa'],
              result.metro_area_id,
              result.metro_area
            );
            geoLevels.push({
              id: result.metro_area_id,
              type: 'msa' as GeoType,
              name: msaName,
            });
          }

          // Country level
          if (result.country_id > 0) {
            const countryName = lookupGeoName(
              geoLists['country'],
              result.country_id,
              result.country
            );
            geoLevels.push({
              id: result.country_id,
              type: 'country' as GeoType,
              name: countryName,
            });
          }

          // Region level
          if (result.region_id > 0) {
            const regionName = lookupGeoName(
              geoLists['region'],
              result.region_id,
              result.region
            );
            geoLevels.push({
              id: result.region_id,
              type: 'region' as GeoType,
              name: regionName,
            });
          }

          setResolved({ geoLevels });
        } else {
          setResolved(EMPTY_LOCATION);
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.warn('Location mapping API failed, will use fallback:', err);
        setResolved(EMPTY_LOCATION);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    resolve();

    return () => controller.abort();
  }, [location, geoLists]);

  return { location: resolved, isLoading };
};
